import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";

/**
 * Homepage promo for the AI Education Advisor.
 *   • default (compact=false) — full-width banner, sits between hero and grids.
 *   • compact=true            — vertical card, drops into a sidebar column.
 */
export default function AiAdvisorBanner({ compact = false }) {
  const bg = "linear-gradient(135deg,#0F172A 0%,#1E293B 55%,#334155 100%)";

  if (compact) {
    return (
      <section
        className="rounded-2xl overflow-hidden relative p-5"
        style={{ background: bg, color: "#F8FAFC" }}
        data-testid="ai-advisor-banner-compact"
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{ background: "radial-gradient(320px circle at 90% 15%, #F0C94833 0%, transparent 65%)" }}
        />
        <div className="relative">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-[0.18em] mb-3"
            style={{ background: "#F0C948", color: "#0F172A" }}
          >
            <Sparkles className="w-3 h-3"/> NEW · AI POWERED
          </span>
          <div
            className="font-black uppercase leading-[1.1] mb-2"
            style={{ color: "#F0C948", fontSize: "18px", letterSpacing: "0.06em" }}
            data-testid="advisor-title-compact"
          >
            EduTime AI<br/>Education Advisor
          </div>
          <p className="text-slate-300 text-[13px] leading-snug mb-4">
            Not sure which course or college is right for you? Get personalised guidance in ~2 minutes.
          </p>
          <Link
            to="/ai-advisor"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-black shadow-md hover:-translate-y-0.5 transition-transform uppercase tracking-wide"
            style={{ background: "#F0C948", color: "#0F172A" }}
            data-testid="advisor-banner-cta-compact"
          >
            Start My Advisory <ArrowRight className="w-3.5 h-3.5"/>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      className="my-10 rounded-3xl overflow-hidden relative"
      style={{ background: bg, color: "#F8FAFC" }}
      data-testid="ai-advisor-banner"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{ background: "radial-gradient(600px circle at 85% 20%, #F0C94833 0%, transparent 60%)" }}
      />
      <div className="relative grid md:grid-cols-[1fr_auto] items-center gap-6 p-8 md:p-12">
        <div>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-4"
            style={{ background: "#F0C948", color: "#0F172A" }}>
            <Sparkles className="w-3 h-3"/> NEW · POWERED BY AI
          </span>
          <div
            className="font-black uppercase mb-2"
            style={{ color: "#F0C948", fontSize: "clamp(14px, 1.4vw, 20px)", letterSpacing: "0.14em" }}
          >
            EDUTIME AI EDUCATION ADVISOR
          </div>
          <h2 className="font-editorial text-3xl md:text-4xl lg:text-5xl font-black leading-[1.05] mb-3">
            Find the Right Course. Choose the Right College. Plan Your Future.
          </h2>
          <p className="text-slate-300 max-w-2xl text-sm md:text-base">
            Not sure which course or college is right for you? Tell EduTime AI about your academic background,
            interests, career goals and budget. Our AI Advisor will help you explore suitable courses and institutions.
          </p>
        </div>
        <Link
          to="/ai-advisor"
          className="justify-self-start md:justify-self-end inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-base font-bold shadow-lg hover:-translate-y-0.5 transition-transform whitespace-nowrap"
          style={{ background: "#F0C948", color: "#0F172A" }}
          data-testid="advisor-banner-cta"
        >
          START YOUR AI ADVISORY <ArrowRight className="w-4 h-4"/>
        </Link>
      </div>
    </section>
  );
}
